import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

const API = process.env.REACT_APP_API_URL || 'https://clicksaver.app';
const ADMIN_EMAIL = 'quazi.hasib13@gmail.com';

// Light theme palette
const C = {
  bg:          '#f6f8fb',
  card:        '#ffffff',
  border:      '#e2e8f0',
  hover:       '#f1f5f9',
  headerBg:    '#ffffff',
  tableHead:   '#f8fafc',
  text:        '#0f172a',
  textSoft:    '#334155',
  textMuted:   '#64748b',
  textFaint:   '#94a3b8',
  primary:     '#2563eb',
  danger:      '#dc2626',
  success:     '#16a34a',
  warning:     '#eab308',
};

async function apiGet(path) {
  const token = await auth.currentUser?.getIdToken();
  return axios.get(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
}

const planColor = (plan) => ({
  free:     '#64748b',
  starter:  '#0284c7',
  pro:      '#7c3aed',
  advanced: '#059669',
})[plan] || '#64748b';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '-'; }
};

const fmtNum = (n) => (n ?? 0).toLocaleString();

function LoginView({ onLogin, error, loading }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 400, background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 32, boxShadow: '0 4px 24px rgba(15,23,42,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#fff' }}>⚙</div>
            <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 20, color: C.text }}>ClickSaver Admin</div>
          </div>
          <div style={{ fontSize: 13, color: C.textMuted }}>Sign in to view all user activity</div>
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: C.danger, fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSoft, marginBottom: 6 }}>Email</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com" autoComplete="email"
          style={{ width: '100%', padding: '10px 14px', fontSize: 14, background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 14, outline: 'none' }} />

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSoft, marginBottom: 6 }}>Password</label>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onLogin(email, password)}
          style={{ width: '100%', padding: '10px 14px', fontSize: 14, background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 12, outline: 'none' }} />

        <button onClick={() => onLogin(email, password)} disabled={loading}
          style={{
            width: '100%', padding: '12px', borderRadius: 8, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
            background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', fontSize: 14, fontWeight: 700,
            opacity: loading ? 0.6 : 1,
          }}>
          {loading ? 'Signing in...' : 'Sign in to Admin'}
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, accent = C.primary }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: C.text, fontFamily: 'monospace' }}>
        <span style={{ color: accent }}>{value}</span>
      </div>
      {hint && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [health, setHealth] = useState(null);

  useEffect(() => {
    apiGet('/api/admin/data-health').then(setHealth).catch(() => {});
  }, []);

  const load = (skipClicks = false, includePaid = false) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (skipClicks) params.set('include_clicks', 'false');
    if (!includePaid) params.set('include_paid', 'false');
    const path = `/api/admin/users?${params.toString()}`;
    apiGet(path)
      .then(d => {
        if (d && d.error) { setError(d.error); setData(d); }
        else { setData(d); setError(null); }
      })
      .catch(e => {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const msg = detail ? `${status || ''} ${detail}` : (e?.message || 'Failed to load admin data');
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  // Render immediately — don't block on slow user/invoice fetch.
  if (error && !data && !health) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, color: C.danger, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, fontFamily: 'Inter, sans-serif', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Failed to load admin data</div>
        <div style={{ fontSize: 13, color: '#ef4444', maxWidth: 600, wordBreak: 'break-word' }}>{error}</div>
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={() => load(false)} style={{ padding: '8px 20px', background: C.primary, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Retry</button>
          <button onClick={() => load(true)} style={{ padding: '8px 20px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Retry without click counts</button>
          <button onClick={onLogout} style={{ padding: '8px 20px', background: C.textSoft, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Sign out</button>
        </div>
      </div>
    );
  }

  const users = (data?.users || []).filter(u => {
    if (planFilter !== 'all' && u.plan !== planFilter) return false;
    if (search && !(u.email || '').toLowerCase().includes(search.toLowerCase()) &&
        !(u.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totals = data?.totals || {};
  const planCounts = totals.plan_counts || {};

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div style={{ background: C.headerBg, borderBottom: `1px solid ${C.border}`, padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#fff' }}>⚙</div>
          <div>
            <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 16, color: C.text }}>ClickSaver Admin</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>Signed in as {auth.currentUser?.email}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => load(false, false)} style={{ padding: '8px 16px', background: C.hover, color: C.textSoft, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Refresh
          </button>
          <button onClick={() => load(false, true)} style={{ padding: '8px 16px', background: C.success, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Load Paid Revenue
          </button>
          <button onClick={onLogout} style={{ padding: '8px 16px', background: C.danger, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Sign out
          </button>
        </div>
      </div>

      <div style={{ padding: 32, maxWidth: 1400, margin: '0 auto' }}>
        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <StatCard label="Total Users" value={fmtNum(totals.users)} accent="#0284c7" />
          <StatCard label="MRR" value={`$${fmtNum(totals.mrr)}`} hint="Active paid plans" accent={C.success} />
          <StatCard label="Total Paid (All Time)" value={`$${((totals.total_paid_cents || 0) / 100).toFixed(2)}`} hint="Sum of paid invoices" accent={C.success} />
          <StatCard label="Paid This Month" value={`$${((totals.total_paid_month_cents || 0) / 100).toFixed(2)}`} hint="This calendar month" accent={C.success} />
          <StatCard label="Clicks This Month" value={fmtNum(totals.clicks_this_month)} accent={C.warning} />
          <StatCard label="Total Campaigns" value={fmtNum(totals.campaigns)} accent="#7c3aed" />
        </div>

        {/* Plan distribution */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Plan Distribution</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {['free', 'starter', 'pro', 'advanced'].map(p => (
              <div key={p} style={{ flex: '1 1 140px', padding: '12px 16px', background: C.bg, border: `1px solid ${planColor(p)}33`, borderRadius: 10 }}>
                <div style={{ fontSize: 11, color: planColor(p), fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{p}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text, fontFamily: 'monospace' }}>{planCounts[p] || 0}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Data health */}
        {health && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Data Health</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              {Object.entries(health).map(([coll, info]) => (
                <div key={coll} style={{ padding: 12, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: 'monospace' }}>{coll}</span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: info.error ? C.danger : C.success, fontFamily: 'monospace' }}>
                      {info.error ? 'ERR' : (info.count ?? 0)}
                    </span>
                  </div>
                  {info.error && <div style={{ fontSize: 11, color: C.danger, wordBreak: 'break-word' }}>{info.error}</div>}
                  {info.by_campaign_id && (
                    <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>
                      {Object.entries(info.by_campaign_id).slice(0, 3).map(([cid, n]) => (
                        <div key={cid} style={{ fontFamily: 'monospace' }}>{cid.slice(0, 12)}… → {n}</div>
                      ))}
                      {Object.keys(info.by_campaign_id).length > 3 && (
                        <div>+{Object.keys(info.by_campaign_id).length - 3} more</div>
                      )}
                    </div>
                  )}
                  {info.sample && info.sample.length > 0 && (
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ fontSize: 10, color: C.textMuted, cursor: 'pointer' }}>sample</summary>
                      <pre style={{ fontSize: 10, color: C.textSoft, margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(info.sample, null, 1)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search email or name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 240px', maxWidth: 320, padding: '9px 14px', fontSize: 13, background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, outline: 'none' }}
          />
          <select value={planFilter} onChange={e => setPlanFilter(e.target.value)}
            style={{ padding: '9px 14px', fontSize: 13, background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, outline: 'none', cursor: 'pointer' }}>
            <option value="all">All plans</option>
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="advanced">Advanced</option>
          </select>
          <div style={{ fontSize: 12, color: C.textMuted, marginLeft: 'auto' }}>
            Showing {users.length} of {data?.users?.length || 0}
          </div>
        </div>

        {/* Users table */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.tableHead }}>
                  <th style={thStyle}></th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Plan</th>
                  <th style={thStyle}>Clicks / Month</th>
                  <th style={thStyle}>Usage</th>
                  <th style={thStyle}>Campaigns</th>
                  <th style={thStyle}>MRR</th>
                  <th style={thStyle}>Paid Total</th>
                  <th style={thStyle}>Google Ads</th>
                  <th style={thStyle}>Country</th>
                  <th style={thStyle}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={11} style={{ textAlign: 'center', padding: 30, color: C.textFaint }}>No users match</td></tr>
                ) : users.map(u => {
                  const usagePercent = u.unlimited ? 0 : (u.click_limit > 0 ? Math.min(100, Math.round((u.clicks_this_month / u.click_limit) * 100)) : 0);
                  const isOpen = expanded.has(u.id);
                  return (
                    <React.Fragment key={u.id}>
                      <tr style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer', background: isOpen ? C.hover : 'transparent' }} onClick={() => toggleExpand(u.id)}>
                        <td style={{ ...tdStyle, width: 30, color: C.textFaint }}>{isOpen ? '▾' : '▸'}</td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600, color: C.text }}>{u.email || '(no email)'}</div>
                          {u.name && <div style={{ fontSize: 11, color: C.textMuted }}>{u.name}</div>}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, textTransform: 'uppercase', background: `${planColor(u.plan)}18`, color: planColor(u.plan) }}>
                            {u.plan}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{fmtNum(u.clicks_this_month)}</td>
                        <td style={tdStyle}>
                          {u.unlimited ? (
                            <span style={{ fontSize: 11, color: C.success }}>Unlimited</span>
                          ) : u.click_limit === 0 ? (
                            <span style={{ fontSize: 11, color: C.textFaint }}>-</span>
                          ) : (
                            <div style={{ minWidth: 120 }}>
                              <div style={{ height: 6, background: C.hover, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${usagePercent}%`, height: '100%', background: usagePercent > 90 ? C.danger : usagePercent > 70 ? C.warning : C.success }} />
                              </div>
                              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 3 }}>{usagePercent}% of {fmtNum(u.click_limit)}</div>
                            </div>
                          )}
                        </td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{u.campaign_count}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', color: u.price_month > 0 ? C.success : C.textFaint }}>${u.price_month}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', color: (u.total_paid || 0) > 0 ? C.success : C.textFaint }}>
                          ${((u.total_paid || 0) / 100).toFixed(2)}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 11, color: u.google_ads_connected ? C.success : C.textFaint }}>
                            {u.google_ads_connected ? '✓ Connected' : '—'}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, fontSize: 12, color: C.textMuted }}>{u.country || '-'}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: C.textMuted }}>{fmtDate(u.created_at)}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={11} style={{ padding: 20, background: C.tableHead, borderTop: `1px solid ${C.border}` }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
                              <div>
                                <div style={detailLabel}>User ID</div>
                                <div style={detailValue}>{u.id}</div>
                              </div>
                              <div>
                                <div style={detailLabel}>Phone</div>
                                <div style={detailValue}>{u.phone || '-'}</div>
                              </div>
                              <div>
                                <div style={detailLabel}>Timezone</div>
                                <div style={detailValue}>{u.timezone || '-'}</div>
                              </div>
                              <div>
                                <div style={detailLabel}>Company</div>
                                <div style={detailValue}>{u.company_name || '-'}</div>
                              </div>
                              <div>
                                <div style={detailLabel}>Billing Email</div>
                                <div style={detailValue}>{u.billing_email || '-'}</div>
                              </div>
                              <div>
                                <div style={detailLabel}>LS Subscription</div>
                                <div style={detailValue}>{u.ls_subscription_id || '-'}</div>
                              </div>
                              <div>
                                <div style={detailLabel}>LS Customer</div>
                                <div style={detailValue}>{u.ls_customer_id || '-'}</div>
                              </div>
                            </div>
                            {u.campaigns && u.campaigns.length > 0 && (
                              <div style={{ marginTop: 20 }}>
                                <div style={{ ...detailLabel, marginBottom: 10 }}>Campaigns ({u.campaigns.length})</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {u.campaigns.map(c => (
                                    <div key={c.id} style={{ display: 'flex', gap: 12, padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
                                      <span style={{ fontWeight: 600, color: C.text, minWidth: 200 }}>{c.domain || '(no domain)'}</span>
                                      <span style={{ color: C.textMuted }}>{c.name || '-'}</span>
                                      <span style={{ marginLeft: 'auto', color: c.is_active ? C.success : C.textFaint, fontSize: 11 }}>{c.is_active ? '● Active' : '○ Inactive'}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1 };
const tdStyle = { padding: '14px 16px', color: C.textSoft, verticalAlign: 'middle' };
const detailLabel = { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 };
const detailValue = { fontSize: 13, color: C.text, fontFamily: 'monospace', wordBreak: 'break-all' };

export default function Admin() {
  const [fbUser, setFbUser] = useState(undefined);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setFbUser(u));
    return unsub;
  }, []);

  const handleLogin = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e) {
      const code = e?.code || '';
      const msg = code === 'auth/wrong-password' || code === 'auth/invalid-credential' ? 'Invalid email or password'
                : code === 'auth/user-not-found' ? 'Admin user not yet provisioned. Wait a moment and try again.'
                : e.message || 'Sign-in failed';
      setError(msg);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  if (fbUser === undefined) {
    return <div style={{ minHeight: '100vh', background: C.bg, color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }

  if (!fbUser) {
    return <LoginView onLogin={handleLogin} error={error} loading={loading} />;
  }

  if ((fbUser.email || '').toLowerCase() !== ADMIN_EMAIL) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, color: C.text, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, fontFamily: 'Inter, sans-serif' }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Access denied</div>
        <div style={{ fontSize: 13, color: C.textMuted }}>Signed in as {fbUser.email} — not an admin account.</div>
        <button onClick={handleLogout} style={{ padding: '10px 24px', background: C.danger, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          Sign out
        </button>
      </div>
    );
  }

  return <AdminDashboard onLogout={handleLogout} />;
}
